import React, { useEffect, useState, useMemo } from "react";
import {
  Activity, GraduationCap, Users2, ClipboardList, Library, FileCheck2,
  Search, ArrowUpDown, ChevronDown, RefreshCw, Calendar, Smartphone,
} from "lucide-react";
import { api } from "../../lib/api";
import TeacherAnalyticsDrawer from "./TeacherAnalyticsDrawer";

/**
 * Admin → Overview tab. Aggregated school-wide KPIs (top stat strip) plus a
 * sortable table of every teacher's activity (groups, students-under-them,
 * resources uploaded, assignments authored, submissions received, last login).
 */
export default function OverviewTab() {
  const [data, setData] = useState({ teachers: [], totals: null });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [sortKey, setSortKey] = useState("groups");
  const [sortDir, setSortDir] = useState("desc");
  const [openTeacher, setOpenTeacher] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/teachers/activity");
      setData(r.data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const rows = useMemo(() => {
    const t = q.trim().toLowerCase();
    let xs = (data.teachers || []).filter((r) =>
      !t || (r.email || "").toLowerCase().includes(t) || (r.name || "").toLowerCase().includes(t)
    );
    xs = [...xs].sort((a, b) => {
      const dir = sortDir === "asc" ? 1 : -1;
      if (sortKey === "name") {
        return dir * String(a.name || a.email).localeCompare(String(b.name || b.email));
      }
      if (sortKey === "last_login_at") {
        return dir * String(a.last_login_at || "").localeCompare(String(b.last_login_at || ""));
      }
      return dir * (((b[sortKey] || 0) - (a[sortKey] || 0)) * -1) * -1; // numeric: bigger first when desc
    });
    return xs;
  }, [data.teachers, q, sortKey, sortDir]);

  const setSort = (k) => {
    if (sortKey === k) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(k); setSortDir("desc"); }
  };

  return (
    <div className="space-y-6" data-testid="admin-overview-tab">
      {/* Top KPI strip */}
      {data.totals && <KpiStrip totals={data.totals} />}

      {/* Toolbar */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex-1 min-w-[220px] relative">
          <Search className="absolute top-1/2 -translate-y-1/2 start-3 w-4 h-4 text-muted-foreground" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search teachers by name or email…"
            data-testid="overview-search"
            className="w-full ps-10 pe-3 py-2.5 rounded-xl border border-border bg-white focus:outline-none focus:border-sage"
          />
        </div>
        <button
          onClick={load}
          className="inline-flex items-center gap-1.5 text-sm bg-white border border-border hover:bg-secondary px-3 py-2 rounded-xl"
          data-testid="overview-refresh"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>

      {/* Table */}
      <div className="bg-white border border-border rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="teachers-activity-table">
            <thead className="bg-secondary/60">
              <tr>
                <Th label="Teacher" sortKey="name" current={sortKey} dir={sortDir} onClick={setSort} />
                <Th label="Groups" sortKey="groups" current={sortKey} dir={sortDir} onClick={setSort} numeric />
                <Th label="Students" sortKey="students" current={sortKey} dir={sortDir} onClick={setSort} numeric />
                <Th label="Resources" sortKey="resources" current={sortKey} dir={sortDir} onClick={setSort} numeric />
                <Th label="Assignments" sortKey="assignments" current={sortKey} dir={sortDir} onClick={setSort} numeric />
                <Th label="Submissions" sortKey="submissions" current={sortKey} dir={sortDir} onClick={setSort} numeric />
                <Th label="Last login" sortKey="last_login_at" current={sortKey} dir={sortDir} onClick={setSort} />
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={7} className="px-6 py-10 text-center text-muted-foreground">Loading teacher activity…</td></tr>
              )}
              {!loading && rows.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-6 py-10 text-center text-muted-foreground" data-testid="overview-empty">
                    {q ? "No teachers match your search." : "No teachers yet. Add one in the Teachers tab."}
                  </td>
                </tr>
              )}
              {!loading && rows.map((r) => <TeacherRow key={r.user_id} r={r} onOpen={() => setOpenTeacher(r.user_id)} />)}
            </tbody>
          </table>
        </div>
      </div>

      {openTeacher && (
        <TeacherAnalyticsDrawer
          teacherId={openTeacher}
          onClose={() => setOpenTeacher(null)}
        />
      )}
    </div>
  );
}

function KpiStrip({ totals }) {
  const cards = [
    { Icon: GraduationCap, label: "Teachers", value: totals.teachers, accent: "bg-sage", testid: "kpi-teachers" },
    { Icon: Activity, label: "Active in 7 days", value: totals.active_last_7d, accent: "bg-emerald-500", testid: "kpi-active" },
    { Icon: Users2, label: "Groups", value: totals.groups, accent: "bg-indigo-500", testid: "kpi-groups" },
    { Icon: GraduationCap, label: "Students grouped", value: totals.students_in_groups, accent: "bg-terracotta", testid: "kpi-students" },
    { Icon: Library, label: "Resources shared", value: totals.resources, accent: "bg-amber-500", testid: "kpi-resources" },
    { Icon: ClipboardList, label: "Assignments", value: totals.assignments, accent: "bg-violet-500", testid: "kpi-assignments" },
    { Icon: FileCheck2, label: "Submissions", value: totals.submissions, accent: "bg-teal-500", testid: "kpi-submissions" },
  ];
  return (
    <div className="grid sm:grid-cols-3 lg:grid-cols-7 gap-3" data-testid="overview-kpi-strip">
      {cards.map(({ Icon, label, value, accent, testid }) => (
        <div key={label} data-testid={testid} className="bg-white border border-border rounded-2xl p-4 flex items-center gap-3">
          <div className={`w-10 h-10 rounded-xl ${accent} text-white flex items-center justify-center shrink-0`}>
            <Icon className="w-5 h-5" strokeWidth={1.7} />
          </div>
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-semibold">{label}</div>
            <div className="font-display text-2xl font-semibold tracking-tight">{value ?? 0}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

function Th({ label, sortKey, current, dir, onClick, numeric }) {
  const active = current === sortKey;
  return (
    <th className={`px-4 py-3 ${numeric ? "text-end" : "text-start"} font-semibold text-xs uppercase tracking-[0.14em] text-muted-foreground`}>
      <button
        onClick={() => onClick(sortKey)}
        data-testid={`sort-${sortKey}`}
        className={`inline-flex items-center gap-1 hover:text-foreground transition ${active ? "text-sage" : ""}`}
      >
        {label}
        {active ? <ChevronDown className={`w-3 h-3 ${dir === "asc" ? "rotate-180" : ""}`} /> : <ArrowUpDown className="w-3 h-3 opacity-40" />}
      </button>
    </th>
  );
}

function TeacherRow({ r, onOpen }) {
  const initials = (r.name || r.email || "?").trim().charAt(0).toUpperCase();
  const lastLogin = r.last_login_at ? timeAgo(r.last_login_at) : "—";
  return (
    <tr
      className="border-t border-border hover:bg-bone/40 cursor-pointer"
      onClick={onOpen}
      data-testid={`teacher-row-${r.user_id}`}
    >
      <td className="px-4 py-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-9 h-9 rounded-full bg-sage/10 text-sage flex items-center justify-center font-semibold shrink-0">
            {initials}
          </div>
          <div className="min-w-0">
            <div className="font-medium leading-tight truncate group-hover:text-sage">{r.name || r.email}</div>
            <div className="text-[11px] text-muted-foreground truncate">{r.email}</div>
          </div>
        </div>
      </td>
      <NumCell value={r.groups} testid={`row-groups-${r.user_id}`} accent={r.groups > 0 ? "text-sage" : ""} />
      <NumCell value={r.students} testid={`row-students-${r.user_id}`} accent={r.students > 0 ? "text-terracotta" : ""} />
      <NumCell value={r.resources} testid={`row-resources-${r.user_id}`} accent={r.resources > 0 ? "text-amber-700" : ""} />
      <NumCell value={r.assignments} testid={`row-assignments-${r.user_id}`} accent={r.assignments > 0 ? "text-indigo-700" : ""} />
      <NumCell value={r.submissions} testid={`row-submissions-${r.user_id}`} accent={r.submissions > 0 ? "text-teal-700" : ""} />
      <td className="px-4 py-3 text-end whitespace-nowrap">
        <span className="inline-flex items-center gap-1.5 text-xs">
          <Calendar className="w-3 h-3 text-muted-foreground" />
          {lastLogin}
        </span>
        {r.devices_bound > 0 && (
          <div className="text-[10px] text-muted-foreground inline-flex items-center gap-1 ms-2">
            <Smartphone className="w-3 h-3" /> {r.devices_bound}
          </div>
        )}
      </td>
    </tr>
  );
}

function NumCell({ value, testid, accent = "" }) {
  return (
    <td className={`px-4 py-3 text-end font-semibold tabular-nums ${accent}`} data-testid={testid}>
      {value ?? 0}
    </td>
  );
}

function timeAgo(iso) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const mins = Math.max(1, Math.floor((Date.now() - d.getTime()) / 60000));
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return d.toLocaleDateString();
}
